import { Box, Flex, Heading, SystemStyleObject, Text } from '@chakra-ui/react';
import { Image } from '@components/AssetWrapper';
import { CopyUrl } from '@components/CopyUrl';
import { BorderWithSquareDots } from '@components/Decorations';
import { YoutubeVideoWithPreviewImage } from '@components/YoutubePlayerModal';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  AppLink,
  Card,
  colors,
  FacebookIcon,
  LANDING_ROUTES,
  pxToRem,
  SHARED_ROUTES,
  TRANSITIONS,
  TwitterIcon,
  useIsClient,
} from '@cyberkongz/ui';
import { PostWithAssets } from '@features/blog/apiContentful';
import { formatDate } from '@features/blog/utils';
import { PageSEOTags } from '@features/seo/SEO';
import { IBlogCategory } from '@typings/generated/contentful';
import { useInView } from 'react-intersection-observer';
// @ts-ignore
import SyntaxHighlighter from 'react-syntax-highlighter';

function PostCategories({ categories }: { categories: IBlogCategory[] }) {
  const categoriesCount = categories.length;

  return (
    <Flex css={{ alignItems: 'center', color: 'textSecondary', gap: 1 }}>
      {categories.map(({ fields: { title, slug } }, i) => (
        <AppLink key={slug} href={LANDING_ROUTES.BLOG_CATEGORY(slug)}>
          <Text textStyle="p-sm" textDecoration="underline">
            {`${title}${i < categoriesCount - 1 ? ',' : ''}`}
          </Text>
        </AppLink>
      ))}
    </Flex>
  );
}

// function PostTags({ categories }: { categories: IBlogCategory[] }) {
//   return (
//     <Flex css={{ alignItems: 'center', pt: 10 }}>
//       <Text css={{ fontWeight: 'medium', color: 'neutral.300', mr: 4 }}>Categories</Text>
//       {categories.map(({ fields: { title, slug } }) => (
//         <AppLink key={slug} href={LANDING_ROUTES.BLOG_CATEGORY(slug)}>
//           <Badge
//             css={{
//               borderColor: 'backgroundTertiary',
//               bg: 'backgroundTertiary',
//             }}
//           >
//             {title}
//           </Badge>
//         </AppLink>
//       ))}
//     </Flex>
//   );
// }

// function RelatedArticles({ relatedArticles }: { relatedArticles: IBlog[] }) {
//   return (
//     <>
//       <Heading size="sm">related articles</Heading>
//       {relatedArticles.map(() => (
//         <Box>test</Box>
//       ))}
//     </>
//   );
// }

function PostFooter() {
  return (
    <>
      <Heading size="md" mt={10}>
        KONGZ TOGETHER STRONG!
      </Heading>
      <Box>
        Join our Discord to always stay up to date and become part of our community:{` `}
        <AppLink href={SHARED_ROUTES.DISCORD}>{SHARED_ROUTES.DISCORD}</AppLink>
      </Box>
      <Image src="/static/blog/logo_footer.png" alt="Cyberkongz logo" />
    </>
  );
}

function PostSocialShareIcons({ css }: { css?: SystemStyleObject }) {
  const isClient = useIsClient();

  return isClient ? (
    <Flex
      direction="column"
      css={{
        position: 'sticky',
        gap: 8,
        maxWidth: pxToRem(1320),
        mx: 'auto',
        transition: TRANSITIONS.standard,
        '& svg': { w: 6 },
        ...css,
      }}
    >
      <AppLink href={`https://facebook.com/sharer.php?u=${window.location.href}`}>
        <FacebookIcon />
      </AppLink>
      <AppLink href={`https://twitter.com/intent/tweet?url=${window.location.href}`}>
        <TwitterIcon />
      </AppLink>
      <CopyUrl />
    </Flex>
  ) : null;
}

export function BlogPost({
  title,
  content,
  dateCreated,
  featuredImage,
  relatedArticles,
  categories,
  excerpt,
}: PostWithAssets) {
  const { ref, inView } = useInView({
    threshold: 0,
    initialInView: true,
    rootMargin: '-100px 0px 0px 0px',
  });

  const featuredImageUrl = featuredImage?.fields?.file?.url;

  return (
    <Flex css={{ alignItems: 'flex-start' }}>
      <PageSEOTags
        title={title}
        description={excerpt}
        imageUrl={featuredImageUrl ? featuredImageUrl.replace('//', 'https://') : undefined}
      />
      <PostSocialShareIcons
        css={{
          opacity: !inView ? 1 : 0,
          pr: 2,
          display: ['none', 'flex'],
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 2,
          m: 0,
        }}
      />
      <Box
        css={{
          maxWidth: ['100%', pxToRem(620), pxToRem(816)],
          mx: 'auto',
          transform: ['none', `translateX(${pxToRem(-32)})`],
        }}
      >
        {categories ? <PostCategories {...{ categories }} /> : null}
        <Heading pb={4}>{title}</Heading>
        <Box css={{ mb: pxToRem(26), color: 'neutral.300' }}>{formatDate(dateCreated)}</Box>
        <Box ref={ref} mb={8} mt={7}>
          <PostSocialShareIcons css={{ flexDirection: 'row' }} />
        </Box>
        <Flex>
          <Card
            variant="landing"
            css={{
              maxWidth: '100%',
              mb: pxToRem(120),
              figcaption: {
                mt: 3,
                color: 'neutral.300',
                fontSize: 'sm',
                textAlign: 'center',
              },
            }}
          >
            {featuredImage ? (
              <Box as="figure" mb={10}>
                <Image src={featuredImage.fields.file?.url} alt={featuredImage.fields.title} />
                <Box as="figcaption">{featuredImage.fields.title}</Box>
              </Box>
            ) : null}
            <Box
              css={{
                '*': {
                  maxWidth: pxToRem(688),
                  mx: 'auto',
                  mb: 5,
                },
                ul: {
                  pl: 4,
                },
                img: {
                  maxWidth: '100%',
                },
                blockquote: {
                  fontStyle: 'italic',
                  borderLeft: `3px solid ${colors.textSecondary}`,
                  pl: 2,
                },
                '& a': { textDecoration: 'underline' },
              }}
            >
              {documentToReactComponents(content, {
                renderNode: {
                  [BLOCKS.HEADING_1]: (node, children) => <Heading size="sm">{children}</Heading>,
                  [BLOCKS.HEADING_2]: (node, children) => <Heading size="xs">{children}</Heading>,
                  [BLOCKS.HEADING_3]: (node, children) => <Heading size="2xs">{children}</Heading>,
                  [BLOCKS.HEADING_4]: (node, children) => <Heading size="3xs">{children}</Heading>,
                  [BLOCKS.PARAGRAPH]: (node, children) => <Box css={{ mb: 5 }}>{children}</Box>,
                  [BLOCKS.HR]: () => (
                    <Box my={10}>
                      <BorderWithSquareDots />
                    </Box>
                  ),
                  [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
                    const figcaption = node.data.target.fields.description;
                    const image = (
                      <Image
                        src={`https://${node.data.target.fields.file.url}`}
                        alt={node.data.target.fields.title}
                        mb={figcaption ? 2 : 10}
                      />
                    );

                    return figcaption ? (
                      <Box as="figure" mb={10}>
                        {image}
                        <Box as="figcaption" textStyle="p-xs">
                          {figcaption}
                        </Box>
                      </Box>
                    ) : (
                      image
                    );
                  },
                  [INLINES.HYPERLINK]: (node, children) => {
                    const { uri } = node.data;
                    const isYoutube = uri.includes('youtube');
                    // const isTwitterStatus = uri.includes('twitter') & uri.includes('status');

                    if (isYoutube) {
                      return <YoutubeVideoWithPreviewImage src={uri} />;
                    }

                    // TO DO, what think on Twitter stuff embed?
                    // if (isTwitterStatus) {
                    //   const twitterId = uri.split('status/')[1];

                    //   if (!twitterId.includes('noEmbed')) {
                    //     return (
                    //       <TwitterTweetEmbed
                    //         tweetId={twitterId}
                    //         options={{ theme: 'dark' }}
                    //         placeholder={<AppSpinner />}
                    //       />
                    //     );
                    //   }
                    // }

                    return <AppLink href={uri}>{children}</AppLink>;
                  },
                },
                renderMark: {
                  [MARKS.CODE]: (children) => <SyntaxHighlighter>{children}</SyntaxHighlighter>,
                },
              })}
              <PostFooter />
            </Box>
            {/* // TO DO LATER TAGS */}
          </Card>
        </Flex>
        {/* TO DO RELATED ARTICLES */}
        {/* {relatedArticles ? <RelatedArticles {...{ relatedArticles }} /> : null} */}
      </Box>
    </Flex>
  );
}
